import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = ["src", "width", "height"]
const _hoisted_2 = ["src"]
const _hoisted_3 = { class: "zoom-controls" }
const _hoisted_4 = { class: "zoom-info" }

import { watchEffect, ref, watch, onMounted, withDefaults, onUnmounted } from '../../../../adapter-vue';
// 导入消息模型接口
import { IMessageModel } from '@tencentcloud/chat-uikit-engine';
// 导入环境判断工具
import { isPC } from '../../../../utils/env';
// 导入图片尺寸处理工具
import { handleSkeletonSize } from '../../utils/utils';
// 导入图片消息内容接口
import type { IImageMessageContent } from '../../../../interface';

// 定义组件事件

export default /*@__PURE__*/_defineComponent({
  __name: 'message-image',
  props: {
    content: { default: () => ({}) },
    messageItem: { default: () => ({} as IMessageModel) }
  },
  emits: ['previewImage'],
  setup(__props: any, { emit: __emit }) {

// 导入必要的Vue组合式API
const emits = __emit;

// 定义组件属性
const props = __props;

// 响应式状态定义
const skeletonDomRef = ref();        // 图片容器引用
const imageData = ref({});           // 图片数据
const showLargeImage = ref(false);   // 是否显示大图
const scale = ref(1);                // 缩放比例
const translate = ref({ x: 0, y: 0 }); // 图片位移
let dragging = false;                // 拖拽状态
let last = { x: 0, y: 0 };          // 上次鼠标位置
const isDragging = ref(false);       // 是否正在拖拽
const imageContainerRef = ref();     // 大图容器引用

// 组件挂载时
onMounted(() => {
  // 如果消息状态为成功或完成，自动调整图片尺寸
  if (
    props.messageItem?.status === 'success'
    || props.messageItem?.status === 'fail'
    || props.messageItem?.progress === 1
  ) {
    autoFixSkeletonSize();
  }
  // 添加滚轮事件监听，用于缩放
  const container = imageContainerRef.value;
  if (container) {
    container.addEventListener('wheel', handleWheel, { passive: false });
  }
});

// 组件卸载时清理事件监听
onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', endDrag);
  const container = imageContainerRef.value;
  if (container) {
    container.removeEventListener('wheel', handleWheel);
  }
});

// 监听图片数据变化
watchEffect(() => {
  // 处理不同格式的图片数据
  if(props.content.MsgType) {
    const ImageInfoArray = props.content?.MsgContent?.ImageInfoArray[0] || {};
    imageData.value = {
      url: ImageInfoArray.URL || '',
      width: ImageInfoArray.Width || '',
      height: ImageInfoArray.Height || '',
    }
  }else {
    imageData.value = props.content
  }
});

// 监听图片高度变化，自动调整尺寸
watch(
  () => props.content.height,
  (newVal: number, oldVal: number) => {
    if (newVal > oldVal) {
      autoFixSkeletonSize();
    }
  },
);

// 自动调整图片尺寸
function autoFixSkeletonSize() {
  const { width = 0, height = 0 } = props.content;
  if (width === 0 || height === 0) return;
  const containerWidth = document.getElementById('app')?.clientWidth || 0;
  const max = !isPC ? Math.min(containerWidth - 180, 300) : 300;
  const size = handleSkeletonSize(width, height, max, max);
  skeletonDomRef?.value?.style && (skeletonDomRef.value.style.width = `${size.width}px`);
  skeletonDomRef?.value?.style && (skeletonDomRef.value.style.height = `${size.height}px`);
}

// 显示大图预览
function toggleShow() {
  if (props.messageItem?.status === 'success' || props.messageItem?.progress === 1) {
    emits('previewImage', props.messageItem);
  }
  showLargeImage.value = true;
}

// 放大图片
function zoomIn() {
  scale.value = Math.min(scale.value + 0.2, 5);
}

// 缩小图片
function zoomOut() {
  scale.value = Math.max(scale.value - 0.2, 0.2);
}

// 重置缩放和位置
function resetZoom() {
  scale.value = 1;
  translate.value = { x: 0, y: 0 };
}

// 开始拖拽
function startDrag(e: MouseEvent) {
  e.preventDefault();
  isDragging.value = true;
  dragging = true;
  last = { x: e.clientX, y: e.clientY };
  document.body.style.cursor = 'grabbing';
  
  // 添加全局鼠标事件监听
  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', endDrag);
}

// 拖拽中
function onDrag(e: MouseEvent) {
  if (!dragging) return;
  e.preventDefault();
  
  const deltaX = e.clientX - last.x;
  const deltaY = e.clientY - last.y;
  
  // 添加边界检查，防止图片被拖出可视区域太远
  const container = imageContainerRef.value;
  if (container) {
    const rect = container.getBoundingClientRect();
    const maxX = rect.width * (scale.value - 1) / 2;
    const maxY = rect.height * (scale.value - 1) / 2;
    
    translate.value.x = Math.max(Math.min(translate.value.x + deltaX, maxX), -maxX);
    translate.value.y = Math.max(Math.min(translate.value.y + deltaY, maxY), -maxY);
  }
  
  last = { x: e.clientX, y: e.clientY };
}

// 结束拖拽
function endDrag() {
  isDragging.value = false;
  dragging = false;
  document.body.style.cursor = 'default';
  
  // 移除全局鼠标事件监听
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', endDrag);
}

// 关闭大图预览
function closeLargeImage() {
  showLargeImage.value = false;
  resetZoom();
}

// 处理鼠标滚轮缩放
function handleWheel(e: WheelEvent) {
  e.preventDefault();
  if (e.deltaY < 0) {
    zoomIn();
  } else {
    zoomOut();
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "skeletonDomRef",
    ref: skeletonDomRef,
    class: "image-container",
    onClick: _withModifiers(toggleShow, ["self"])
  }, [
    _createElementVNode("img", {
      class: _normalizeClass(['message-image', !_unref(isPC) && 'message-image-h5']),
      src: _unref(imageData).url,
      width: _unref(imageData).width,
      height: _unref(imageData).height,
      onClick: _withModifiers(toggleShow, ["stop"])
    }, null, 10, _hoisted_1),
    (_unref(showLargeImage))
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "large-image-modal",
          onClick: closeLargeImage
        }, [
          _createElementVNode("div", {
            class: "large-image-wrapper",
            onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("div", {
              class: "image-container",
              ref_key: "imageContainerRef",
              ref: imageContainerRef
            }, [
              _createElementVNode("div", {
                class: "image-drag-container",
                style: _normalizeStyle({
              transform: `scale(${_unref(scale)}) translate(${_unref(translate).x}px, ${_unref(translate).y}px)`,
              cursor: _unref(isDragging) ? 'grabbing' : 'grab'
            }),
                onMousedown: startDrag
              }, [
                _createElementVNode("img", {
                  src: _unref(imageData).url,
                  class: "large-image",
                  draggable: "false"
                }, null, 8, _hoisted_2)
              ], 36)
            ], 512),
            _createElementVNode("div", {
              class: "close-btn",
              onClick: closeLargeImage
            }, "×"),
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", { class: "zoom-btns" }, [
                _createElementVNode("button", {
                  class: "zoom-btn",
                  onClick: zoomIn,
                  title: "放大"
                }, _cache[1] || (_cache[1] = [
                  _createElementVNode("i", { class: "icon-zoom-in" }, null, -1)
                ])),
                _createElementVNode("button", {
                  class: "zoom-btn",
                  onClick: zoomOut,
                  title: "缩小"
                }, _cache[2] || (_cache[2] = [
                  _createElementVNode("i", { class: "icon-zoom-out" }, null, -1)
                ])),
                _createElementVNode("button", {
                  class: "zoom-btn",
                  onClick: resetZoom,
                  title: "重置"
                }, _cache[3] || (_cache[3] = [
                  _createElementVNode("i", { class: "icon-reset" }, null, -1)
                ]))
              ]),
              _createElementVNode("div", _hoisted_4, _toDisplayString(Math.round(_unref(scale) * 100)) + "%", 1)
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ], 512))
}
}

})